// Dependencies
import * as Yup from "yup";
import { FC } from "react";
import { useSnackbar } from "notistack";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";

// Components
import { Form, Formik } from "formik";
import { Grid, Typography } from "@mui/material";
import { GoogleButton, FacebookButton } from "~/components/Common/Button";
import Button from "~/components/Common/Button";
import AuthLink from "~/components/Auth/AuthLink";
import AuthTitle from "~/components/Auth/AuthTitle";
import AuthGrid from "~/components/Auth/AuthFormLayout";
import FormInput from "~/components/Auth/Registration/FormInput";
import FormErrorMessage from "~/components/Common/FormErrorMessage";
import FormPasswordInput from "~/components/Auth/FormPasswordInput";
import FormCheckbox from "~/components/Auth/Registration/FormCheckbox";

// Routes
import config from "~/config";

// api
import { login } from "~/services/auth";

// Types
import { TQueryError } from "~/types";
import { formValidate } from "~/enums";

// Styles
import {
  StyledButton,
  StyledFormLink,
  StyledLoginIcon,
  StyledSignUpLink,
  StyledLoginButton,
} from "./LoginForm.styled";

interface LoginFormProps {}

type loginFormValues = {
  username: string;
  password: string;
};

const LoginForm: FC<LoginFormProps> = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const loginMutation = useMutation({
    mutationFn: login,
    onSuccess: () => {
      enqueueSnackbar("Login success!", { variant: "success" });
      navigate(config.routes.dashboard);
    },
    onError: (error: TQueryError) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const handleSubmitForm = (values: loginFormValues) => {
    loginMutation.mutate({
      username: values.username,
      password: values.password,
    });
  };

  return (
    <Formik
      initialValues={{
        username: "",
        password: "",
        rememberPass: false,
      }}
      // validate form values
      validationSchema={Yup.object({
        username: Yup.string()
          .required("User name is required")
          .matches(formValidate.userName.regex, formValidate.userName.message),
        password: Yup.string()
          .required("Password is required")
          .matches(formValidate.password.regex, formValidate.password.message),
      })}
      // handle submit form
      onSubmit={handleSubmitForm}
    >
      {({ values, errors, touched, handleChange, handleBlur }) => (
        <Form>
          <AuthGrid>
            <Grid item>
              <AuthTitle>Welcome back</AuthTitle>
              <Typography variant="h3">
                Welcome back Money Lover! Please enter your details
              </Typography>
            </Grid>
            <Grid item>
              <StyledLoginIcon />
            </Grid>

            <FormInput
              label="Username"
              type="text"
              name="username"
              placeholder="Enter your username"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.username}
            />
            <FormErrorMessage
              hasError={!!(errors.username && touched.username)}
              error={errors.username}
            />

            <FormPasswordInput
              label="Password"
              name="password"
              placeholder="Enter your password"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.password}
              autoComplete="false"
            />
            <FormErrorMessage
              hasError={!!(errors.password && touched.password)}
              error={errors.password}
            />

            <StyledFormLink item>
              <FormCheckbox
                label="Remember"
                name="rememberPass"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.rememberPass}
              />
              <AuthLink href={"/forgot-password"}>Forgot my password</AuthLink>
            </StyledFormLink>
            <StyledLoginButton>
              <Button
                type={"submit"}
                loading={loginMutation.isLoading}
                variant={"contained"}
              >
                Login
              </Button>
            </StyledLoginButton>
            <StyledButton>
              <GoogleButton>Login with Google</GoogleButton>
            </StyledButton>
            <StyledButton>
              <FacebookButton>Login with Facebook</FacebookButton>
            </StyledButton>

            <StyledSignUpLink item>
              <Typography variant={"h3"}>
                Don't have account?
                <AuthLink href={config.routes.registration}>
                  &nbsp;Sign up for free
                </AuthLink>
              </Typography>
            </StyledSignUpLink>
          </AuthGrid>
        </Form>
      )}
    </Formik>
  );
};

export default LoginForm;
